/**
 * Which two events the Profile radar shows: "current" and "next".
 *
 * ONE function for the app and the events admin page, so what the admin sees
 * as "automatically selected" is exactly what the trainer's radar draws.
 *
 * Rule, in order:
 *   1. Only events the radar watches: the trainer's play-style categories
 *      (lib/eventRadar), minus anything the admin EXCLUDED, plus anything the
 *      admin PINNED (a pin beats the category set). Finished events never.
 *   2. Current: a pinned event that is running now, shortest first; else
 *      lib/todayFocus (device clock, background items never, shortest live
 *      event wins).
 *   3. Next: a pinned event still to start, soonest first; else the soonest
 *      event still to start; else the first upcoming-bucket event left.
 *
 * The admin switches (migration 0085) never move an event's timings: exclude
 * only hides it from this pick, pin only promotes it here.
 */
import { eventMatchesRadar } from "./eventRadar";
import { eventEndMs, eventStartMs, isEventOver } from "./eventRecency";
import { pickTodayFocus, type FocusEventLike } from "./todayFocus";

export type RadarOverride = "pin" | "exclude";

export interface RadarEventLike extends FocusEventLike {
  rawType?: string;
  radar?: RadarOverride | null;
}

export interface RadarPick<E> {
  current: E | null;
  next: E | null;
  /** Everything the radar considered, current/next included, feed order. */
  eligible: E[];
}

export function radarEligible<E extends RadarEventLike>(
  ev: E,
  categories: ReadonlySet<string>,
  now: number,
): boolean {
  if (ev.radar === "exclude") return false;
  if (isEventOver(ev, now)) return false;
  return ev.radar === "pin" || eventMatchesRadar({ category: ev.category ?? undefined, rawType: ev.rawType }, categories);
}

export function pickRadarEvents<E extends RadarEventLike>(
  active: readonly E[],
  upcoming: readonly E[],
  categories: ReadonlySet<string>,
  now: number = Date.now(),
): RadarPick<E> {
  const liveActive = active.filter((ev) => radarEligible(ev, categories, now));
  const liveUpcoming = upcoming.filter((ev) => radarEligible(ev, categories, now));
  const seen = new Set<string>();
  const eligible: E[] = [];
  for (const ev of [...liveActive, ...liveUpcoming]) {
    if (seen.has(ev.id)) continue;
    seen.add(ev.id);
    eligible.push(ev);
  }

  const runningNow = (ev: E) => eventStartMs(ev) <= now && now < eventEndMs(ev);
  const pinnedNow = eligible
    .filter((ev) => ev.radar === "pin" && runningNow(ev))
    .sort((a, b) => eventEndMs(a) - eventStartMs(a) - (eventEndMs(b) - eventStartMs(b)));
  const current = pinnedNow[0] ?? pickTodayFocus(liveActive, liveUpcoming, now);

  const rest = eligible.filter((ev) => ev.id !== current?.id);
  const pinnedLater = rest
    .filter((ev) => ev.radar === "pin" && eventStartMs(ev) > now)
    .sort((a, b) => eventStartMs(a) - eventStartMs(b));
  const next =
    pinnedLater[0] ??
    rest.find((ev) => eventStartMs(ev) > now) ??
    rest.find((ev) => liveUpcoming.includes(ev)) ??
    null;
  return { current, next, eligible };
}
